import { AllocationRule } from 'further-types/lp/allocations';
import {
  ConsiderationAllocationLogic,
  ConsiderationType,
  PartialRealisationAllocationLogic,
} from 'further-types/lp/realisation';
import { CurrencyCode, FxDataFeed } from 'further-types/lp/vehicle';
import { z } from 'zod';
import { CalendarDay } from '../../../utils';
import { withPaginationParams } from '../../helpers';
import isMongoId from '../../helpers/is-mongo-id';
import { IndividualTransaction } from '../transaction';
import validateInvestmentsHaveLcyValues from './validate-investments-have-lcy-values';

const ExtendedIndividualTransaction = IndividualTransaction.extend({
  investmentId: z
    .string()
    .refine(isMongoId({ optional: true }), {
      message: 'Invalid investment ID',
    })
    .optional(),
  holdingClassId: z
    .string()
    .refine(isMongoId({ optional: true }), {
      message: 'Invalid holding class ID',
    })
    .optional(),
  isUnrealisedAssetFxGainOrLossUnwind: z.boolean().optional(),
});

const considerationAmounts = z.object({
  receivedConsiderationPerUnit: z.number().optional(),
  receivedConsiderationTotal: z.number().optional(),
  escrowConsiderationPerUnit: z.number().optional(),
  escrowConsiderationTotal: z.number().optional(),
  earnoutConsiderationPerUnit: z.number().optional(),
  earnoutConsiderationTotal: z.number().optional(),
  withholdingTaxPayable: z.number().optional(),
});

const allocationSchema = z.object({
  commitmentId: z.string().refine(isMongoId(), {
    message: 'Invalid commitment ID',
  }),
  investorLabel: z.string(),
  allocationAmountTcy: z.number(),
  allocationPercentage: z.number(),
  excludeFromAllocation: z.boolean(),
  withholdingTaxExempt: z.boolean().optional(),
  commitment: z.object({
    totalCommitmentLcy: z.number(),
  }),
});

const holdingClassRealisationSchema = z.object({
  holdingClassId: z.string().refine(isMongoId(), {
    message: 'Please select a holding class',
  }),
  name: z.string(),
  unitsSold: z
    .number()
    .gt(0, {
      message: 'Units sold must be greater than 0',
    })
    .optional(),
  partialRealisationAllocationLogic: z.nativeEnum(
    PartialRealisationAllocationLogic,
  ),
  cashConsiderationAmounts: considerationAmounts.optional(),
  unitsAllocationPerInvestment: z
    .array(
      z.object({
        investmentId: z.string().refine(isMongoId(), {
          message: 'Invalid investment ID',
        }),

        totalInvestmentAmountTcy: z.number(),
        totalInvestmentAmountLcy: z.number().nullable(),
        totalInvestmentNumberOfUnits: z.number(),
        currencyRate: z.object({
          fetchedRate: z.number().optional(),
          fetchedSource: z.nativeEnum(FxDataFeed).optional(),
          overrideRate: z.number().optional(),
          overrideSource: z.string().optional(),
        }),
        holdingClassId: z.string().refine(isMongoId(), {
          message: 'Invalid holding class ID',
        }),
        name: z.string(),
        allocatedUnits: z.number(),
        date: z.instanceof(CalendarDay),
        currentNumberOfUnits: z.number(),
        totalNumberOfUnits: z.number(),
        currency: z.nativeEnum(CurrencyCode),
        investmentAmountTcy: z.number(),
        investmentAmountLcy: z.number(),
        unitPriceTcy: z.number(),
        currentUnrealisedValue: z.number(),
        currentMOIC: z.string(),
        allocations: z.array(allocationSchema).optional(),
      }),
    )
    .optional(),
});

const investorAllocationSchema = z.object({
  allocationType: z.nativeEnum(AllocationRule),
  investmentId: z.string().refine(isMongoId(), {
    message: 'Invalid investment ID',
  }),
  commitmentId: z.string().refine(isMongoId(), {
    message: 'Invalid commitment ID',
  }),
  investmentDate: z.instanceof(CalendarDay),
  holdingClassId: z.string().refine(isMongoId(), {
    message: 'Invalid holding class ID',
  }),
  unitsAllocated: z.number(),
  investorName: z.string(),
  holdingClassName: z.string(),
  allocationPercentage: z.number(),
  originalAllocationPercentage: z.number(),
  receivedConsideration: z.number(),
  earnoutConsideration: z.number(),
  escrowConsideration: z.number(),
  withholdingTaxPayable: z.number(),
  withholdingTaxExempt: z.boolean().optional(),
});

const considerationSchema = z.object({
  considerationType: z.nativeEnum(ConsiderationType),
  considerationAllocationLogic: z.nativeEnum(ConsiderationAllocationLogic),
  currency: z.nativeEnum(CurrencyCode),
  proRataConsideration: considerationAmounts.optional(),
  investorAllocations: z.array(investorAllocationSchema).optional(),
});

const investorWithholdingTaxSchema = z.object({
  commitmentId: z.string().refine(isMongoId(), {
    message: 'Invalid commitment ID',
  }),
  totalCommitmentLcy: z.number(),
  withholdingTaxExempt: z.boolean(),
  investorName: z.string(),
});

export const CreateRealisationFormSchema = z
  .object({
    vehicleId: z.string().refine(isMongoId(), {
      message: 'Invalid vehicle ID',
    }),
    assetId: z.string().refine(isMongoId(), {
      message: 'Please select an asset',
    }),
    assetName: z.string(),
    ledgerDate: z
      .instanceof(CalendarDay)
      .refine((date) => date instanceof CalendarDay, {
        message: 'Please select a valid realisation date',
      }),
    holdingClassRealisation: z.array(holdingClassRealisationSchema).min(1),
    consideration: z.array(considerationSchema),
    investorWithholdingTaxList: z
      .array(investorWithholdingTaxSchema)
      .optional(),
    transactions: z.array(ExtendedIndividualTransaction).optional(),
    currencyRate: z
      .object({
        fetchedRate: z.number().optional(),
        fetchedSource: z.nativeEnum(FxDataFeed).optional(),
        overrideRate: z.number().optional(),
        overrideSource: z.string().optional(),
      })
      .optional(),
    threeCyCurrencyRate: z
      .object({
        fetchedRate: z.number().optional(),
        fetchedSource: z.nativeEnum(FxDataFeed).optional(),
        overrideRate: z.number().optional(),
        overrideSource: z.string().optional(),
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    validateInvestmentsHaveLcyValues(data, ctx);
  });

const considerationAmountsTcy = z.object({
  receivedConsiderationPerUnitTcy: z.number().optional(),
  receivedConsiderationTotalTcy: z.number().optional(),
  escrowConsiderationPerUnitTcy: z.number().optional(),
  escrowConsiderationTotalTcy: z.number().optional(),
  earnoutConsiderationPerUnitTcy: z.number().optional(),
  earnoutConsiderationTotalTcy: z.number().optional(),
  withholdingTaxPayableTcy: z.number().optional(),
});

export const CreateRealisationInputSchema = z.object({
  vehicleId: z.string().refine(isMongoId(), {
    message: 'Invalid vehicle ID',
  }),
  assetId: z.string().refine(isMongoId(), {
    message: 'Please select an asset',
  }),
  ledgerDate: z
    .instanceof(CalendarDay)
    .refine((date) => date instanceof CalendarDay, {
      message: 'Please select a valid realisation date',
    }),
  currencyCode: z.nativeEnum(CurrencyCode),
  currencyRate: z
    .object({
      fetchedRate: z.number().optional(),
      fetchedSource: z.nativeEnum(FxDataFeed).optional(),
      overrideRate: z.number().optional(),
      overrideSource: z.string().optional(),
    })
    .optional(),
  holdingClassRealisationAllocations: z.array(
    z.object({
      holdingClassId: z.string().refine(isMongoId(), {
        message: 'Invalid holding class ID',
      }),
      unitsSold: z.number(),
      partialRealisationAllocationLogic: z.nativeEnum(
        PartialRealisationAllocationLogic,
      ),
      unitsAllocationPerInvestment: z.array(
        z.object({
          investmentId: z.string().refine(isMongoId(), {
            message: 'Invalid investment ID',
          }),
          investmentCurrency: z.nativeEnum(CurrencyCode),
          allocatedUnits: z.number(),
          receivedConsiderationTotalTcy: z.number(),
          escrowConsiderationTotalTcy: z.number(),
          earnoutConsiderationTotalTcy: z.number(),
          withholdingTaxPayableTcy: z.number(),
          receivedConsiderationTotalLcy: z.number(),
          escrowConsiderationTotalLcy: z.number(),
          earnoutConsiderationTotalLcy: z.number(),
          withholdingTaxPayableLcy: z.number(),
        }),
      ),
    }),
  ),
  considerationEntries: z.array(
    z.object({
      considerationType: z.nativeEnum(ConsiderationType),
      considerationAllocationLogic: z.nativeEnum(ConsiderationAllocationLogic),
      currencyCode: z.nativeEnum(CurrencyCode),
      proRataConsiderationAmounts: considerationAmountsTcy.optional(),
      investorAllocations: z.array(
        z.object({
          investmentId: z.string().refine(isMongoId(), {
            message: 'Invalid investment ID',
          }),
          commitmentId: z.string().refine(isMongoId(), {
            message: 'Invalid commitment ID',
          }),
          holdingClassId: z.string().refine(isMongoId(), {
            message: 'Invalid holding class ID',
          }),
          investorLabel: z.string(),
          allocationType: z.nativeEnum(AllocationRule),
          unitsAllocated: z.number(),
          allocationPercentage: z.number(),
          originalAllocationPercentage: z.number(),
          receivedConsiderationTcy: z.number(),
          earnoutConsiderationTcy: z.number(),
          escrowConsiderationTcy: z.number(),
          withholdingTaxPayableTcy: z.number(),
          withholdingTaxExempt: z.boolean().optional(),
        }),
      ),
      perHoldingClassConsiderationAmounts: z.array(
        considerationAmountsTcy.extend({
          holdingClassId: z.string().refine(isMongoId(), {
            message: 'Invalid holding class ID',
          }),
        }),
      ),
    }),
  ),
  transactions: z.array(ExtendedIndividualTransaction),
});

export type CreateRealisationFormValues = z.infer<
  typeof CreateRealisationFormSchema
>;

// Other services

export const ListRealisationsInputSchema = withPaginationParams(
  z.object({
    vehicleId: z.string().refine(isMongoId(), {
      message: 'Invalid vehicle ID',
    }),
    searchQuery: z.string().optional(),
    dateRange: z
      .object({
        startDate: z.instanceof(CalendarDay),
        endDate: z.instanceof(CalendarDay),
      })
      .optional(),
  }),
  [
    'dateRange',
    'searchQuery',
    'ledgerDate',
    'assetName',
    'status',
    'numberOfUnitsRealised',
    'totalRealisationAmountTcy',
    'totalRealisationAmountLcy',
  ],
);

export const DeleteRealisationSchema = z.object({
  realisationId: z.string().refine(isMongoId(), {
    message: 'Invalid realisation ID',
  }),
  vehicleId: z.string().refine(isMongoId(), {
    message: 'Invalid vehicle ID',
  }),
});

export const CalculateRealisationSummaryInputSchema = z.object({
  assetId: z.string().refine(isMongoId(), {
    message: 'Invalid asset ID',
  }),
  realisationDate: z
    .instanceof(CalendarDay)
    .refine((date) => date instanceof CalendarDay, {
      message: 'Please select a valid realisation date',
    }),
  currentRealisationPerHoldingClassInvestment: z.array(
    z.object({
      holdingClassId: z.string().refine(isMongoId(), {
        message: 'Invalid holding class ID',
      }),
      investmentId: z.string().refine(isMongoId(), {
        message: 'Invalid investment ID',
      }),
      unitsSold: z.number(),
    }),
  ),
  holdingClassInvestments: z.array(
    z.object({
      holdingClassId: z.string().refine(isMongoId(), {
        message: 'Invalid holding class ID',
      }),
      investmentId: z.string().refine(isMongoId(), {
        message: 'Invalid investment ID',
      }),
      investmentAmountTcy: z.number(),
      investmentAmountLcy: z.number(),
      totalNumberOfUnits: z.number(),
      allocatedUnits: z.number(),
      date: z.instanceof(CalendarDay),
      currencyRate: z.object({
        fetchedRate: z.number().optional(),
        fetchedSource: z.nativeEnum(FxDataFeed).optional(),
        overrideRate: z.number().optional(),
        overrideSource: z.string().optional(),
      }),
    }),
  ),
});

export type CalculateRealisationSummaryInputValues = z.infer<
  typeof CalculateRealisationSummaryInputSchema
>;

export const GenerateAllocationInputSchema = z.object({
  holdingClassRealisations: z.array(holdingClassRealisationSchema),
  withholdingTaxList: z.array(investorWithholdingTaxSchema),
});

export const GetRealisationSchema = z.object({
  realisationId: z.string().refine(isMongoId(), {
    message: 'Invalid realisation ID',
  }),
  vehicleId: z.string().refine(isMongoId(), {
    message: 'Invalid vehicle ID',
  }),
});

const currencyRateSchema = z.object({
  fetchedRate: z.number().optional(),
  fetchedSource: z.nativeEnum(FxDataFeed).optional(),
  overrideRate: z.number().optional(),
  overrideSource: z.string().optional(),
});

export const GetUnitAllocationsInputSchema = z.object({
  assetId: z.string().refine(isMongoId(), { message: 'Invalid asset ID' }),
  vehicleId: z.string().refine(isMongoId(), { message: 'Invalid vehicle ID' }),
  holdingClassId: z.string().refine(isMongoId(), {
    message: 'Invalid holding class ID',
  }),
  unitsSold: z.number().gt(0, { message: 'Units sold must be greater than 0' }),
  partialRealisationAllocationLogic: z.nativeEnum(
    PartialRealisationAllocationLogic,
  ),
  date: z.instanceof(CalendarDay).optional(),
});

export const BuildTransactionsInputSchema = z.object({
  vehicleId: z.string().refine(isMongoId(), {
    message: 'Invalid vehicle ID',
  }),
  assetId: z.string().refine(isMongoId(), {
    message: 'Invalid asset ID',
  }),
  realisationDate: z
    .instanceof(CalendarDay)
    .refine((date) => date instanceof CalendarDay, {
      message: 'Please select a valid realisation date',
    }),
  holdingClassRealisation: z.array(holdingClassRealisationSchema),
  realisationCurrencyRate: currencyRateSchema.nullable(),
  threeCyRealisationCurrencyRate: currencyRateSchema.nullable(),
  realisationCurrencyCode: z.nativeEnum(CurrencyCode),
});
